import { Component, OnInit, OnDestroy, ViewChildren, Output, EventEmitter } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import * as fromWorkflow from 'src/app/modules/communications/store/workflow';

import { GlobalEventListenerService } from 'src/app/modules/core/services/global-event-listener.service';
import { WorkflowStep, WorkflowStepAction, WorkflowStepConfiguration, WorkflowStepRule } from 'src/app/modules/management/store/workflow';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Workflow,
  WorkflowStepActionTypes,
  WorkflowStepTeamMember,
  WorkflowStepTeamMemberType,
  WorkflowStepTypes,
  WorkflowStatus
} from 'src/app/modules/core/store/models/workflow.model';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import produce from 'immer';

@Component({
    selector: 'topo-workflow-edit',
    templateUrl: './workflow-edit.component.html',
    styleUrls: ['./workflow-edit.component.scss'],
    standalone: false
})
export class WorkflowEditComponent implements OnInit, OnDestroy {
  @ViewChildren('statusSelect') statusSelectControls;
  @ViewChildren('actionSelect') actionSelectControls;

  @Output() changed = new EventEmitter();

  destroy$ = new Subject<void>();
  workflow: Workflow;
  status$: Observable<WorkflowStatus[]>;
  stepForDelete: boolean;
  actionForDelete$: Observable<WorkflowStepAction>;
  ruleForDelete$: Observable<WorkflowStepRule>;
  isActive: boolean = false;
  WorkflowStepTypes = WorkflowStepTypes;
  WorkflowStepActionTypes = WorkflowStepActionTypes;
  workflowStepUIdToEdit: string = null;

  workflowStepActionUIdToEdit: string = null;
  workflowStepActionStepUIdToEdit: string = null;

  selectMembers: boolean = false;
  isDrawerOpen: boolean = false;
  isDirty: boolean = false;
  workflowName: string;
  workflowDesc: string;
  isWorkflowActive: boolean;
  isWorkflowDefault: boolean;
  showConfirmClose: boolean;
  showConfirmValidation: boolean;
  validationMessage: string;
  validationList: any;
  teamMembers: any;

  deleteContext: {
    step?: WorkflowStep;
    action?: WorkflowStepAction;
    rule?: WorkflowStepRule;
    member?: WorkflowStepTeamMember;
  } = null;

  constructor(
    private store: Store<fromWorkflow.State>,
    private eventListenerService: GlobalEventListenerService,
    private translateService: TranslateService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.store.dispatch(
      fromWorkflow.getStatuses({
        queryParams: {
          skip: 0,
          take: 100,
          where: 'IsActive==true'
        }
      })
    );

    this.store
      .select(fromWorkflow.selectSelectedWorkflow)
      .pipe(takeUntil(this.destroy$))
      .subscribe(workflow => {
        const wasActive = this.isActive;
        this.isActive = !!workflow;
        this.workflow = workflow;
        if (wasActive !== this.isActive) {
          this.isDrawerOpen = true;
          this.workflowName = workflow?.name;
          this.workflowDesc = workflow?.description;
          this.isWorkflowActive = workflow?.isActive === true;
          this.isWorkflowDefault = workflow?.isDefault === true;
        }
      });

    this.store
      .select(fromWorkflow.selectIsDirty)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isDirty => {
        this.isDirty = isDirty;
      });

    this.status$ = this.store.select(fromWorkflow.selectStatuses);
    this.actionForDelete$ = this.store.select(fromWorkflow.selectWorkflowStepActionForDelete);
    this.ruleForDelete$ = this.store.select(fromWorkflow.selectWorkflowStepRuleForDelete);
    this.store
      .select(fromWorkflow.selectWorkflowStepForDelete)
      .pipe(takeUntil(this.destroy$))
      .subscribe(wfStep => {
        if (wfStep) {
          this.stepForDelete = true;
        } else {
          this.stepForDelete = false;
        }
      });

    // listen for click events. if the user clicks outside the menu
    // and the menu is open then toggle the display.
    const clickEventSub = this.eventListenerService.clickEvents$.pipe(takeUntil(this.destroy$)).subscribe(target => {
      for (const control of this.statusSelectControls?._results) {
        if (!control.nativeElement.contains(target) && control.nativeElement.classList.contains('is-active')) {
          control.nativeElement.classList.remove('is-active');
        }
      }

      for (const control of this.actionSelectControls?._results) {
        if (!control.nativeElement.contains(target) && control.nativeElement.classList.contains('is-active')) {
          control.nativeElement.classList.remove('is-active');
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close() {
    if (this.isDirty === true) {
      this.showConfirmClose = true;
    } else {
      this.store.dispatch(fromWorkflow.setSelectedWorkflow({ workflow: null }));
    }
  }

  onConfirmClose(response: boolean): void {
    if (response) {
      this.store.dispatch(fromWorkflow.setSelectedWorkflow({ workflow: null }));
    }
    this.showConfirmClose = false;
  }

  checkWorkflowActive(): void {
    this.isWorkflowActive = !this.isWorkflowActive;
    this.setChanged();
  }

  checkWorkflowDefault(): void {
    this.isWorkflowDefault = !this.isWorkflowDefault;
    this.setChanged();
  }

  onSaveAndClose(): void {
    this.validationList = [];

    if (!(this.workflowName?.trim().length > 0)) {
      this.validationList.push(this.translateService.instant('MANAGEMENT.WORKFLOW.INVALID-NAME'));
    }

    this.workflow?.workflowSteps?.forEach(s => {
      if (s.workflowStepType !== WorkflowStepTypes.Complete && !(s.actions?.length > 0) || s.actions?.some(a => !!!a.destinationStepUId)) {
        const message = this.translateService.instant('MANAGEMENT.WORKFLOW.MISSING-DEST-STEP', { name: s.name });
        this.validationList.push(message);
      }
      if (s.workflowStepType === WorkflowStepTypes.Approval && !(s.teamMembers?.length > 0)) {
        const message = this.translateService.instant('MANAGEMENT.WORKFLOW.MISSING-TEAM-MEMBERS', { name: s.name });
        this.validationList.push(message);
      }
    });

    if (this.validationList.length > 0) {
      const message = this.translateService.instant('MANAGEMENT.WORKFLOW.CONFIRM-VALIDATION-BELOW-SAVE');
      this.validationMessage = message;
      this.showConfirmValidation = true;
    } else {
      this.saveAndClose();
    }
  }

  private saveAndClose() {
    if (!(this.workflowName?.trim().length > 0)) {
      this.workflowName = this.translateService.instant(`MANAGEMENT.WORKFLOW.TEMPLATE-DEFAULT-NAME-${this.workflow.parentType}`);
    }
    const workflow: Workflow = {
      ...this.workflow,
      name: this.workflowName,
      description: this.workflowDesc,
      isActive: this.isWorkflowActive,
      isDefault: this.isWorkflowDefault
    };

    this.store.dispatch(fromWorkflow.setSelectedWorkflow({ workflow }));
    this.store.dispatch(fromWorkflow.saveSelectedWorkflow({ close: true }));
  }

  onConfirmValidation(response: boolean): void {
    if (response) {
      this.saveAndClose();
    }

    this.showConfirmValidation = false;
  }

  setChanged(): void {
    this.store.dispatch(fromWorkflow.setIsDirty({ isDirty: true }));
  }

  getIsAllAtOnce(step: WorkflowStep): boolean {
    return step?.configuration?.allTeamMembersAtOnce == true;
  }

  allAtOnceChecked(step: WorkflowStep, event: any): void {
    let allAtOnce = step?.configuration?.allTeamMembersAtOnce == true;

    let configuration: WorkflowStepConfiguration = {
      ...step.configuration
    };

    configuration.allTeamMembersAtOnce = !allAtOnce;

    const workflowStep: WorkflowStep = {
      ...step,
      configuration: configuration
    };

    this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));
  }

  selectedMembersChanged(step: WorkflowStep, member: any): void {
    this.selectMembers = false;

    if (!step.teamMembers?.some(m => m.memberUId == member.uId)) {
      const newStep = produce(step, draftState => {
        if (!!!draftState.teamMembers) {
          draftState.teamMembers = [];
        }

        draftState.teamMembers.push({
          uId: uuidv4(),
          workflowStepUId: draftState.uId,
          sortOrder: draftState.teamMembers.length + 1,
          memberUId: member.uId,
          memberDisplayName: member.displayName,
          type:
            member.type === this.translateService.instant('MANAGEMENT.WORKFLOW.TEAM-MEMBER-TYPE-USERS')
              ? WorkflowStepTeamMemberType.User
              : WorkflowStepTeamMemberType.Group
        });
      });

      this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep: newStep }));
    }
  }

  deleteMemberClick(step: WorkflowStep, member: WorkflowStepTeamMember): void {
    this.deleteContext = {
      step: step,
      member: member
    };
  }

  onConfirmDelete(response: boolean): void {
    if (response && !!this.deleteContext.step) {
      if (!!this.deleteContext.member) {
        this.deleteMember(this.deleteContext.step, this.deleteContext.member);
      } else if (!!this.deleteContext.action) {
        this.deleteAction(this.deleteContext.step, this.deleteContext.action);
      } else if (!!this.deleteContext.rule) {
        this.deleteRule(this.deleteContext.step, this.deleteContext.rule);
      } else {
        this.deleteStep(this.deleteContext.step);
      }
    }
    this.deleteContext = null;
  }

  deleteMember(step: WorkflowStep, member: WorkflowStepTeamMember): void {
    const index = step.teamMembers?.findIndex(m => m.uId === member.uId);

    if (index >= 0) {
      const newStep = produce(step, draftState => {
        draftState.teamMembers.splice(index, 1);
      });
      this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep: newStep }));
    }
  }

  addMemberClick(): void {
    this.teamMembers = [];
    this.selectMembers = true;
  }

  editStepClick(step: WorkflowStep): void {
    this.workflowStepUIdToEdit = step.uId;
  }

  onStepEditSave(editStep: WorkflowStep): void {
    this.workflowStepUIdToEdit = null;

    if (editStep.workflowStepType === WorkflowStepTypes.Approval) {
      if (!!!editStep.actions) {
        editStep.actions = [];
      }
      if (!editStep.actions.find(a => a.actionType === WorkflowStepActionTypes.Approve)) {
        editStep.actions.push({
          uId: uuidv4(),
          sortOrder: editStep.actions.length + 1,
          name: '',
          description: '',
          iconName: '',
          actionType: WorkflowStepActionTypes.Approve,
          destinationStepUId: this.workflow?.workflowSteps?.find(s => s.workflowStepType === WorkflowStepTypes.Send)?.uId,
          transitionViewUId: null
        });
      }
      if (!editStep.actions.find(a => a.actionType === WorkflowStepActionTypes.Reject)) {
        editStep.actions.push({
          uId: uuidv4(),
          sortOrder: editStep.actions.length + 1,
          name: '',
          description: '',
          iconName: '',
          actionType: WorkflowStepActionTypes.Reject,
          destinationStepUId: this.workflow?.workflowSteps?.find(s => s.workflowStepType !== WorkflowStepTypes.Send)?.uId,
          transitionViewUId: null
        });
      }
    } else {
      editStep.actions?.forEach(a => (a.actionType = WorkflowStepActionTypes.NotSet));
    }

    const stepsToUpdate = [];

    const currentStep = this.workflow?.workflowSteps.find(s => s.uId === editStep.uId);
    if (currentStep.configuration?.initialStep !== editStep.configuration?.initialStep) {
      if (editStep?.configuration.initialStep === true) {
        this.workflow?.workflowSteps
          .filter(s => s.uId !== editStep.uId && s.configuration?.initialStep === true)
          .forEach(s => {
            const workflowStep: WorkflowStep = {
              ...s,
              configuration: {
                initialStep: false
              }
            };
            stepsToUpdate.push(workflowStep);
          });
      }
    }
    if (currentStep.configuration?.completeStep !== editStep.configuration?.completeStep) {
      if (editStep?.configuration.completeStep === true) {
        this.workflow?.workflowSteps
          .filter(s => s.uId !== editStep.uId && s.configuration?.completeStep === true)
          .forEach(s => {
            const workflowStep: WorkflowStep = {
              ...s,
              configuration: {
                completeStep: false
              }
            };
            stepsToUpdate.push(workflowStep);
          });
      }
    }

    stepsToUpdate.push(editStep);
    stepsToUpdate.forEach(s => {
      this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep: s }));
    });
  }

  onStepEditCancel(step: WorkflowStep): void {
    this.workflowStepUIdToEdit = null;
  }

  onStepEditDelete(step: WorkflowStep): void {
    this.workflowStepUIdToEdit = null;
    this.deleteContext = {
      step: step
    };
  }

  editStepActionClick(step: WorkflowStep, action: WorkflowStepAction): void {
    if (!!this.workflowStepActionUIdToEdit) {
      this.workflowStepActionUIdToEdit = null;
    } else {
      this.workflowStepActionStepUIdToEdit = step.uId;
      this.workflowStepActionUIdToEdit = action.uId;
    }
  }

  onStepActionEditSave(editAction: WorkflowStepAction): void {

    this.workflowStepActionUIdToEdit = null;

    const currentStep = this.workflow?.workflowSteps?.find(s => s.uId === this.workflowStepActionStepUIdToEdit);
    const currentStepAction = currentStep?.actions?.find(s => s.uId === editAction.uId);

    const actionsToUpdate = [];

    if (currentStepAction.configuration?.primaryAction !== editAction.configuration?.primaryAction) {
      if (editAction?.configuration?.primaryAction === true) {
        currentStep?.actions
          .filter(a => a.uId !== editAction.uId && a.configuration?.primaryAction === true)
          .forEach(a => {
            const workflowStepAction: WorkflowStepAction = {
              ...a,
              configuration: {
                primaryAction: false
              }
            };
            actionsToUpdate.push(workflowStepAction);
          });
      }
    }

    actionsToUpdate.push(editAction);
    actionsToUpdate.forEach(a => {
      this.store.dispatch(fromWorkflow.updateWorkflowStepAction({ workflowStep: currentStep, stepAction: a }));
    });
  }

  onStepActionEditCancel(editAction: WorkflowStepAction): void {
    this.workflowStepActionUIdToEdit = null;
  }

  getEditableActions(step: WorkflowStep): WorkflowStepAction[] {
    return step?.actions.filter(a => !!!a.actionType);
  }

  getNonEditableActions(step: WorkflowStep): WorkflowStepAction[] {
    return step?.actions.filter(a => a.actionType != null && a.actionType !== WorkflowStepActionTypes.NotSet);
  }

  getActionTypeImage(action: WorkflowStepAction): string {
    return action.actionType === WorkflowStepActionTypes.Approve ? 'assets/images/approved.svg' : 'assets/images/rejected.svg';
  }

  getActionTypeTitle(action: WorkflowStepAction): string {
    return action.actionType === WorkflowStepActionTypes.Approve
      ? this.translateService.instant('MANAGEMENT.WORKFLOW.APPROVE')
      : this.translateService.instant('MANAGEMENT.WORKFLOW.REJECT');
  }

  statusItemSelected(step: WorkflowStep, status: WorkflowStatus) {
    const workflowStep: WorkflowStep = {
      ...step,
      workflowStatusUId: status.uId,
      statusName: status.name,
      statusHtmlColorCode: status.htmlColorCode
    };

    this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));
  }

  actionItemSelected(workflowStep: WorkflowStep, action: WorkflowStepAction, destinationStep: WorkflowStep) {
    const stepAction: WorkflowStepAction = {
      ...action,
      destinationStepUId: destinationStep.uId,
    };

    this.store.dispatch(fromWorkflow.updateWorkflowStepAction({ workflowStep, stepAction }));
  }

  actionDrop(workflowStep: WorkflowStep, dragDropEvent: any): void {
    this.store.dispatch(
      fromWorkflow.moveWorkflowStepAction({
        workflowStep,
        previousIndex: dragDropEvent.previousIndex,
        currentIndex: dragDropEvent.currentIndex
      })
    );
  }

  stepDrop(dragDropEvent: any): void {
    this.store.dispatch(
      fromWorkflow.moveWorkflowStep({
        previousIndex: dragDropEvent.previousIndex,
        currentIndex: dragDropEvent.currentIndex
      })
    );
  }

  selectStatus(workflowStep: WorkflowStep, status: WorkflowStatus, elm: any): void {
    elm.classList.remove('is-active');
    const wfs: WorkflowStep = {
      ...workflowStep,
      workflowStatusUId: status.uId,
      statusName: status.name,
      statusHtmlColorCode: status.htmlColorCode
    };
    this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep: wfs }));
  }

  getDestinationStepColor(action: WorkflowStepAction): string {
    const step = this.workflow?.workflowSteps?.find(ws => ws.uId === action?.destinationStepUId);
    if (!step) {
      return '#EFEFEF';
    }
    return step.statusHtmlColorCode;
  }

  getActionName(action: WorkflowStepAction): string {
    return this.getDestinationStepName(action);
  }

  getDestinationStepName(action: WorkflowStepAction): string {
    return this.workflow?.workflowSteps?.find(ws => ws.uId === action?.destinationStepUId)?.statusName ?? '';
  }

  isFirstWorkflowStep(workflowStep: WorkflowStep): boolean {
    return workflowStep?.configuration?.initialStep === true;
  }

  isLastWorkflowStep(workflowStep: WorkflowStep): boolean {
    return workflowStep?.configuration?.completeStep === true;
  }

  selectAsInitialStep(step: WorkflowStep): void {
    let configuration: WorkflowStepConfiguration = {
      ...step.configuration
    };

    if (!!step.configuration && step.configuration.initialStep !== undefined) {
      configuration.initialStep = !step.configuration.initialStep;
    } else {
      configuration.initialStep = true;
    }

    const workflowStep: WorkflowStep = {
      ...step,
      configuration: configuration
    };

    this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));

    if (configuration.initialStep === true) {
      this.workflow?.workflowSteps
        .filter(s => s.uId !== step.uId && s.configuration?.initialStep === true)
        .forEach(s => {
          const workflowStep: WorkflowStep = {
            ...s,
            configuration: {
              initialStep: false
            }
          };
          this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));
        });
    }
  }

  selectAsCompleteStep(step: WorkflowStep): void {
    let configuration: WorkflowStepConfiguration = {
      ...step.configuration
    };

    if (!!step.configuration && step.configuration.completeStep !== undefined) {
      configuration.completeStep = !step.configuration.completeStep;
    } else {
      configuration.completeStep = true;
    }

    const workflowStep: WorkflowStep = {
      ...step,
      configuration: configuration
    };

    this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));

    if (configuration.completeStep === true) {
      this.workflow?.workflowSteps
        .filter(s => s.uId !== step.uId && s.configuration?.completeStep === true)
        .forEach(s => {
          const workflowStep: WorkflowStep = {
            ...s,
            configuration: {
              completeStep: false
            }
          };
          this.store.dispatch(fromWorkflow.updateWorkflowStep({ workflowStep }));
        });
    }
  }

  deleteWorkflowStepClick(workflowStep: WorkflowStep): void {
    this.deleteContext = {
      step: workflowStep
    };
  }

  deleteStep(workflowStep: WorkflowStep): void {
    this.store.dispatch(fromWorkflow.setWorkflowStepForDelete({ workflowStep }));
    this.store.dispatch(fromWorkflow.deleteWorkflowStep());
    this.store.dispatch(fromWorkflow.clearWorkflowStepForDelete());
  }

  deleteActionClick(workflowStep: WorkflowStep, action: WorkflowStepAction): void {
    this.deleteContext = {
      step: workflowStep,
      action: action
    };
  }

  deleteAction(step: WorkflowStep, action: WorkflowStepAction): void {
    this.store.dispatch(fromWorkflow.setWorkflowActionForDelete({ workflowStep: step, action }));
    this.store.dispatch(fromWorkflow.deleteWorkflowStepAction());
    this.store.dispatch(fromWorkflow.clearWorkflowStepActionForDelete());
  }

  closeDialog() {
    this.store.dispatch(fromWorkflow.clearWorkflowStepForDelete());
  }

  selectStepForAction(workflowStep: WorkflowStep, stepAction: WorkflowStepAction, destinationWorkflowStep: WorkflowStep, elm: any): void {
    elm.classList.remove('is-active');
    const updatedAction: WorkflowStepAction = {
      ...stepAction,
      destinationStepUId: destinationWorkflowStep.uId
    };
    this.store.dispatch(fromWorkflow.updateWorkflowStepAction({ workflowStep, stepAction: updatedAction }));
  }

  addWorkflowStep(): void {
    this.store.dispatch(fromWorkflow.addWorkflowStep());
  }

  addActionClick(workflowStep, action): void {
    this.store.dispatch(fromWorkflow.addWorkflowStepAction({ workflowStep }));
  }

  scrollToStep(workflowStep: WorkflowStep): void {
    const el = document.getElementById(workflowStep.uId);
    if (!!el) {
      el.scrollIntoView();
    }
  }

  addRuleClick(step): void {
    this.store.dispatch(fromWorkflow.addWorkflowStepRule({ step: step }));
  }
  editRuleClick(step, rule): void {
    this.store.dispatch(fromWorkflow.editWorkflowStepRule({ step: step, rule: rule }));
  }

  deleteRuleClick(step, rule): void {
    this.deleteContext = {
      step: step,
      rule: rule
    };
  }

  deleteRule(step: WorkflowStep, rule: WorkflowStepRule): void {
    this.store.dispatch(fromWorkflow.setWorkflowStepRuleForDelete({ step: step, rule }));
    this.store.dispatch(fromWorkflow.deleteSelectedWorkflowStepRule());
    this.store.dispatch(fromWorkflow.clearWorkflowStepRuleForDelete());
  }

  ruleDrop(workflowStep: WorkflowStep, dragDropEvent: any): void {
    this.store.dispatch(
      fromWorkflow.moveWorkflowStepRule({
        workflowStep,
        previousIndex: dragDropEvent.previousIndex,
        currentIndex: dragDropEvent.currentIndex
      })
    );
  }

  memberDrop(workflowStep: WorkflowStep, dragDropEvent: any): void {
    this.store.dispatch(
      fromWorkflow.moveWorkflowStepMember({
        workflowStep,
        previousIndex: dragDropEvent.previousIndex,
        currentIndex: dragDropEvent.currentIndex
      })
    );
  }

  drawerOpenClose(open: boolean): void {
    this.isDrawerOpen = open;
  }
}
